import { useMemo, useRef, useEffect } from "react";

import { css } from "@compiled/react";
import { UniformRichText } from "@uniformdev/canvas-react";

import { token } from "@atlaskit/tokens";

import { Nullable } from "@/content/templates/template.types";
import {
    BgColors,
    linkColors
} from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { DEFAULT_LOCALE, LOCALES, useLocale } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import type { LinkNode, RichTextNode } from "@uniformdev/richtext";

export type RichTextComponentProps = {
    text?: Nullable<string>;
    uniformParameter?: string;
    bgColor?: BgColors;
};

// Link renderer component - defined outside of RichTextComponent to avoid re-definition on render
// This is used with memoization to prevent DOM reconciliation issues with context providers
const LinkRenderer = ({
    node,
    children,
    dataEventProps,
    localizeLinkUrl
}: {
    node: RichTextNode;
    children?: React.ReactNode;
    dataEventProps: DataEventProps;
    localizeLinkUrl: (url: string) => string;
}) => {
    let path: string = (node as LinkNode)["link"].path;
    const absolutePathRegex =
        /https:\/\/www\.atlassian\.com\/(?![a-z]{2}\/)(?<urlSlug>.+?)/g;
    if (absolutePathRegex.test(path)) {
        path = path.replace(
            absolutePathRegex,
            (_match, _p1, _offset, _string, groups) => {
                return `/${localizeLinkUrl(groups.urlSlug)}`;
            }
        );
    } else {
        const relativePathRegex = /^\/(.*)/;
        path = path.replace(relativePathRegex, (_match, _p1) => {
            console.log("p1", _p1);
            return `/${localizeLinkUrl(_p1)}`;
        });
    }
    return (
        <a href={path} {...dataEventProps}>
            {children}
        </a>
    );
};

// Factory function that creates a bound link renderer with injected dependencies
// This prevents component definition in render while maintaining memoization
const createLinkRendererWithContext = (
    dataEventProps: DataEventProps,
    localizeLinkUrl: (url: string) => string
) => {
    const BoundLinkRenderer = ({
        node,
        children
    }: {
        node: RichTextNode;
        children?: React.ReactNode;
    }) => (
        <LinkRenderer
            node={node}
            dataEventProps={dataEventProps}
            localizeLinkUrl={localizeLinkUrl}
        >
            {children}
        </LinkRenderer>
    );
    return BoundLinkRenderer;
};

export const RichTextComponent = ({
    text,
    uniformParameter,
    bgColor
}: RichTextComponentProps) => {
    // NOTE: Nested selectors are necessary here to be able to style the dangerouslySetInnerHTML blob
    const richTextCss = css({
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p": {
            margin: "0"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p + p": {
            // will only apply to paragraphs that immediately follow another paragraph
            marginBlockStart: "16px"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "a": {
            "textDecoration": "underline",
            "textUnderlineOffset": `${token("space.025")}`,
            "color": linkColors[bgColor || "bgWhite"].defaultColor,
            "&:visited": {
                color: linkColors[bgColor || "bgWhite"].visitedColor
            },
            "&:hover, &:focus": {
                color: linkColors[bgColor || "bgWhite"].hoverColor,
                textShadow: ""
            },
            "&:visited:hover, &:visited:focus": {
                color: linkColors[bgColor || "bgWhite"].visitedHoverColor
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h1 > b": {
            // we don't want to apply bold font to headings
            fontWeight: "800" // comes from the design system
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h2, h3, h4, h5, h6 > b": {
            // we don't want to apply bold font to headings
            fontWeight: "700" // comes from the design system
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["h1, h2, h3, h4, h5, h6"]: {
            marginBlockEnd: token("space.050")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["h1, h2"]: {
            marginBlockStart: token("space.600")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["h3"]: {
            marginBlockStart: token("space.400")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["h4, h5, h6"]: {
            marginBlockStart: token("space.300")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "ul, ol": {
            margin: 0
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "table": {
            borderCollapse: "collapse",
            borderSpacing: "0",
            border: "0",
            emptyCells: "hide",
            display: "block"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "thead": {
            border: "0",
            display: "table-header-group"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "th": {
            padding: "8px",
            textAlign: "left"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "tbody": {
            display: "table-row-group"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "tr": {
            border: "0",
            display: "table-row"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "td": {
            textAlign: "left",
            borderColor: wacToken("color.neutral.dark1100"),
            borderStyle: "solid",
            borderWidth: "1px",
            padding: token("space.200")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".table-wrapper": {
            display: "block",
            overflowX: "auto",
            marginBlockEnd: token("space.800")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "table p": {
            marginBlockStart: "0",
            marginBlockEnd: "0"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "code": {
            "backgroundColor": wacToken("color.neutral.light200"),
            "color": wacToken("color.primary.black"),
            "paddingBlock": token("space.025"),
            "paddingInline": token("space.075"),
            "fontSize": "0.875rem",
            "borderRadius": "5px",

            "&.code-block": {
                padding: 0
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "pre": {
            "overflowX": "auto",
            "backgroundColor": wacToken("color.neutral.light200"),
            "borderRadius": "6px",
            "marginBlock": token("space.200"),
            "position": "relative",
            "counterReset": "line",

            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".line-number": {
                "display": "inline-block",
                "width": "30px",
                "textAlign": "right",
                "paddingBlock": token("space.025"),
                "paddingInlineEnd": token("space.050"),
                "marginInlineEnd": token("space.200"),
                "position": "relative",
                "backgroundColor": wacToken("color.neutral.dark1100"),
                "color": wacToken("color.neutral.dark600"),

                "&::after": {
                    content: '""',
                    position: "absolute",
                    insetBlockStart: "0",
                    insetInlineEnd: "-8px",
                    width: "8px",
                    height: "100%",
                    backgroundColor: wacToken("color.neutral.dark1000")
                }
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".code-line[data-first-line] .line-number": {
                paddingBlockStart: token("space.100"),
                borderStartStartRadius: "6px"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".code-line[data-last-line] .line-number": {
                paddingBlockEnd: token("space.100"),
                borderEndStartRadius: "6px"
            }
        }
    });

    const containerRef = useRef<HTMLDivElement | null>(null);

    const localeCode = useLocale();

    const blogUrlsRegex = /^blog\/.+/;

    const dataEventProps: DataEventProps = {
        "data-product-key": undefined,
        "data-event-component": "linkButton",
        "data-event": "clicked",
        "data-event-container": "bodyContentInline"
    };

    const localizeLinkUrl = (url: string) => {
        return localeCode === DEFAULT_LOCALE || blogUrlsRegex.test(url) // don't want to localize /blog/* urls
            ? url
            : `${localeCode}/${url}`;
    };

    const html = useMemo(() => {
        let initialHtml = text || "";

        // Hacky stuff pt1: Need to fix relative localized links
        const relativeLinkRegex = /(?:\"https:\/\/\/)(?<urlSlug>.+?)\"/g;
        initialHtml = initialHtml.replace(
            relativeLinkRegex,
            (_match, _p1, _offset, _string, groups) => {
                return `"/${localizeLinkUrl(groups.urlSlug)}"`;
            }
        );

        // similar to above, but for relative anchor links, fix but don't localize them
        const relativeAnchorLinkRegex = /(?:\"https:\/\/\#)(?<anchor>.+?)\"/g;
        initialHtml = initialHtml.replace(
            relativeAnchorLinkRegex,
            (_match, _p1, _offset, _string, groups) => {
                return `"#${groups.anchor}"`;
            }
        );

        // handle case where link is relative and not localized
        const relativeUrlRegex = new RegExp(
            `href="\\/(${localeCode}\\/)?([^"]*)"`,
            "g"
        );
        initialHtml = initialHtml.replace(
            relativeUrlRegex,
            (_match, _p1, _p2) => {
                // If already localized, return the match as is
                const href = `/${_p2}`;
                const alreadyLocalized =
                    LOCALES.some((locale) => href.startsWith(`/${locale}/`)) ||
                    LOCALES.some((locale) => href === `/${locale}`);
                // If already localized, return the match as is
                if (alreadyLocalized) return _match;
                // Otherwise, prepend the current locale code to the URL
                return `href="/${localizeLinkUrl(_p2)}"`;
            }
        );

        // handle case where absolute WAC links are not localized
        const absolutePathRegex =
            /\"https:\/\/www\.atlassian\.com\/(?![a-z]{2}\/)(?<urlSlug>.+?)\"/g;
        initialHtml = initialHtml.replace(
            absolutePathRegex,
            (_match, _p1, _offset, _string, groups) => {
                return `"/${localizeLinkUrl(groups.urlSlug)}"`;
            }
        );

        // Hacky stuff pt2: replace &amp;nbsp; with &nbsp;
        initialHtml = initialHtml
            .replace(/&amp;nbsp;/g, "&nbsp;")
            .replace(/&amp;amp;/g, "&amp;");

        // Move leading and trailing whitespace outside anchor tags
        // This handles cases where Contentful includes spaces in link text
        // Spaces are preserved but moved outside the link so they're not underlined
        initialHtml = initialHtml.replace(
            /<a\s*([^>]*)>([\s\S]*?)<\/a>/g,
            (_match, attributes, innerContent) => {
                // Extract leading whitespace (spaces, tabs, newlines, non-breaking spaces)
                const leadingWhitespaceMatch =
                    innerContent.match(/^([\s\u00A0]+)/);
                const leadingWhitespace = leadingWhitespaceMatch
                    ? leadingWhitespaceMatch[1]
                    : "";
                // Extract trailing whitespace
                const trailingWhitespaceMatch =
                    innerContent.match(/([\s\u00A0]+)$/);
                const trailingWhitespace = trailingWhitespaceMatch
                    ? trailingWhitespaceMatch[1]
                    : "";
                // Get trimmed inner content
                const trimmed = innerContent
                    .replace(/^[\s\u00A0]+/, "")
                    .replace(/[\s\u00A0]+$/, "");
                // Preserve attributes with proper spacing
                const attrs = attributes.trim();
                const dataAttrs = Object.entries(dataEventProps)
                    .map(([key, value]) => `${key}="${value}"`)
                    .join(" ");
                const anchorTag = `<a ${
                    attrs ? `${attrs} ` : ""
                }${dataAttrs}>${trimmed}</a>`;
                // Return with whitespace moved outside the anchor tag
                return `${leadingWhitespace}${anchorTag}${trailingWhitespace}`;
            }
        );

        initialHtml = initialHtml.replace(
            /<pre\s*([^>]*)>\s*<code\s*([^>]*)>([\s\S]*?)<\/code>\s*<\/pre>/g,
            (_match, preAttributes, codeAttributes, innerContent) => {
                // Preserve <pre> attributes with proper spacing
                const preAttrs = preAttributes.trim();
                const preOpenTag = preAttrs ? `<pre ${preAttrs}>` : `<pre>`;
                // Preserve <code> attributes with proper spacing
                const codeAttrs = codeAttributes.trim();
                const codeOpenTag = codeAttrs
                    ? `<code class="code-block" ${codeAttrs}>`
                    : `<code class="code-block">`;
                const lines = innerContent.split(/\r\n|\n|\r/i);
                const wrapped = lines
                    .map((line: string, index: number) => {
                        const isFirst = index === 0;
                        const isLast = index === lines.length - 1;
                        const dataAttrs = [
                            isFirst ? "data-first-line" : "",
                            isLast ? "data-last-line" : ""
                        ]
                            .filter(Boolean)
                            .join(" ");
                        // Make <html> within code block display as text
                        const escapedLine = line
                            .replace(/&/g, "&amp;")
                            .replace(/</g, "&lt;")
                            .replace(/>/g, "&gt;")
                            .replace(/"/g, "&quot;");
                        return `<span class="code-line"${
                            dataAttrs ? ` ${dataAttrs}` : ""
                        }><span class="line-number" aria-hidden="true">${
                            index + 1
                        }</span><span class="line-content">${escapedLine}</span></span>`;
                    })
                    .join("\n");
                return `${preOpenTag}${codeOpenTag}${wrapped}</code></pre>`;
            }
        );

        return initialHtml;
    }, [text, localeCode]);

    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;
        // Transform function — wraps each "line" inside code blocks with <span>
        const processCodeBlocks = () => {
            // @ts-ignore - dataset spanned property access
            root.querySelectorAll("pre > code").forEach((codeNode) => {
                const el = codeNode as HTMLElement;
                // Avoid double-processing - check if already processed by regex or DOM manipulation
                if (el.dataset["spanned"] === "1") return;
                // Check if already processed by regex replacement (contains code-line structure)
                if (el.querySelector(".code-line")) return;
                if (!el.classList.contains("code-block")) {
                    el.classList.add("code-block");
                }
                const raw = el.innerHTML;
                const lines = raw.split(/<br\s*\/?>|\r\n|\n|\r/i);
                const wrapped = lines
                    .map((line: string, index: number) => {
                        const isFirst = index === 0;
                        const isLast = index === lines.length - 1;
                        const dataAttrs = [
                            isFirst ? "data-first-line" : "",
                            isLast ? "data-last-line" : ""
                        ]
                            .filter(Boolean)
                            .join(" ");
                        return `<span class="code-line"${
                            dataAttrs ? ` ${dataAttrs}` : ""
                        }><span class="line-number" aria-hidden="true">${
                            index + 1
                        }</span><span class="line-content">${line}</span></span>`;
                    })
                    .join("\n");
                el.innerHTML = wrapped;
                el.dataset["spanned"] = "1";
            });
        };

        const processAll = () => {
            processCodeBlocks();
        };

        processAll();
        // Observe DOM changes so we catch async content rendered by UniformRichText
        const mo = new MutationObserver(() => {
            processAll();
        });
        mo.observe(root, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, [uniformParameter, text]);

    // Memoized renderer that uses the factory function to create a bound component
    // This ensures the component itself is never defined during render
    const memoizedLinkRenderer = useMemo(
        () => createLinkRendererWithContext(dataEventProps, localizeLinkUrl),
        [localeCode]
    );

    // Memoized renderer resolver to prevent recreation on each render
    // This prevents React from treating the renderer as a new node each time,
    // which was causing insertBefore errors with context providers
    const memoizedRichTextRenderer = useMemo(
        () => (richTextNode: RichTextNode) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((richTextNode as any).type === "link") {
                return memoizedLinkRenderer;
            }
            return undefined;
        },
        [memoizedLinkRenderer]
    );

    return uniformParameter ? (
        <div css={richTextCss} ref={containerRef}>
            <UniformRichText
                parameterId={uniformParameter}
                resolveRichTextRenderer={memoizedRichTextRenderer}
            />
        </div>
    ) : (
        <div
            css={richTextCss}
            ref={containerRef}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};
